/**
 * shpflt-color-siblings.js
 *
 * Intercepts clicks on colour-sibling swatches and swaps the entire <product-info>
 * element via the Section Rendering API instead of triggering a full page navigation.
 *
 * Progressive enhancement: if JS is unavailable, or if the <product-info> ancestor is
 * not found (e.g. the standalone section outside a PDP), the <a href> navigates normally.
 *
 * Architecture decision — why a standalone script (not a method inside product-info.js):
 * - product-info.js already handles variant-level swaps within the same product; adding
 *   cross-product sibling navigation there would mix two distinct responsibilities.
 * - The snippet's root <div class="shpflt-color-siblings"> has no JS hooks of its own,
 *   so event delegation from this script on the document is the least-invasive approach.
 * - The script is only loaded when siblings exist (see product-information-blocks.liquid),
 *   so it adds zero overhead on single-colour products.
 *
 * Risks documented in the PR / final report:
 * - Analytics: a pushState navigation does NOT fire a full pageview. Any GA4 / Meta Pixel
 *   page_view event needs a custom router hook (see ShpfltColorSiblings.onNavigate callback
 *   stub at the bottom of this file).
 * - Double-click: handled via AbortController — in-flight request is cancelled and a new
 *   one starts immediately.
 * - Sold-out / unpublished sibling: the fetch returns 200 with the section HTML; if the
 *   new product-info has no purchasable variant, product-info.js will disable the buy
 *   button as usual. A 404 (deleted product) triggers the window.location fallback.
 * - popstate: handled — back/forward navigation re-fetches the URL's product section.
 */

(function () {
  'use strict';

  // ── Re-execution guard ──────────────────────────────────────────────────────
  // This IIFE is loaded as a classic <script defer> tag *inside* <product-info>.
  // HTMLUpdateUtility.viewTransition (theme.js) uses setInnerHTML to re-create
  // every <script> element before inserting the new <product-info> into the live
  // document — dynamically-inserted scripts ignore `defer` and execute
  // immediately, so this IIFE runs again on every sibling swap.
  //
  // Without a guard, each swap accumulates another document click listener with
  // its own independent `activeController` closure.  On the second user click
  // all N listeners fire; each starts a separate fetch that can neither abort
  // nor coordinate with the others, producing duplicate viewTransition calls and
  // a corrupt DOM (two simultaneous <product-info> elements, duplicate pubsub
  // subscriptions, stale pointer-events lockout).
  //
  // Fix: set window.ShpfltColorSiblings BEFORE registering any listeners (so the
  // flag is visible on the very next execution), then bail out immediately if it
  // is already set.  One listener set survives across all swaps, regardless of
  // how many times the script re-executes.
  if (window.ShpfltColorSiblings) return;

  // ── Public extension point ──────────────────────────────────────────────────
  // Set BEFORE listener registration so the guard above is valid on every
  // subsequent re-execution of this script.
  //
  // Override ShpfltColorSiblings.onNavigate in your theme customisation scripts
  // to fire analytics pageviews on sibling swaps.
  //
  // Example (GA4):
  //   ShpfltColorSiblings.onNavigate = function(url, title) {
  //     gtag('event', 'page_view', { page_location: url, page_title: title });
  //   };
  window.ShpfltColorSiblings = {
    onNavigate: function (/* url, title */) {},
  };

  /** Active AbortController for any in-flight sibling fetch. */
  let activeController = null;

  /**
   * Fetch the section HTML for a sibling product and morph <product-info>.
   * @param {HTMLAnchorElement} anchor - the clicked swatch <a>
   */
  function swapSibling(anchor) {
    const href = anchor.getAttribute('href');
    if (!href) return false;

    const productInfo = anchor.closest('product-info');
    if (!productInfo) return false; // standalone section — let browser navigate

    // Sibling's own title, read from the clicked swatch: the Section Rendering API
    // response is a section fragment, never a <head>, so there is no <title> to parse.
    const siblingTitle = anchor.dataset.title || anchor.getAttribute('aria-label') || href;

    // Abort any previous in-flight request, then track THIS request's own controller so
    // the .finally() below only clears loading state for the request that is still current.
    if (activeController) activeController.abort();
    const thisController = new AbortController();
    activeController = thisController;

    const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    const sep = href.includes('?') ? '&' : '?';
    const requestUrl = `${href}${sep}section_id=${sectionId}`;

    // Disable pointer events on the info panel while loading
    productInfo.classList.add('shpflt-siblings-loading');

    fetch(requestUrl, { signal: thisController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newProductInfo = doc.querySelector('product-info');
        if (!newProductInfo) throw new Error('product-info not found in response');

        // Update browser history and page title BEFORE morphing so any
        // popstate listener that fires immediately sees the correct URL.
        document.title = siblingTitle;
        history.pushState({ shpfltSiblingHref: href }, '', href);

        // Morph the entire <product-info> element (mirrors handleSwapProduct in
        // product-info.js when updateFullPage is false)
        HTMLUpdateUtility.viewTransition(productInfo, newProductInfo);

        // Notify analytics / third-party integrations
        ShpfltColorSiblings.onNavigate(href, siblingTitle);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; // user clicked another swatch — silent
        // Network error or 404: fall back to a real page navigation
        window.location.href = href;
      })
      .finally(function () {
        // Only this request's own controller may clear the loading state — a request
        // aborted by a newer click must not clear loading while the newer one is in flight.
        if (activeController === thisController) {
          productInfo.classList.remove('shpflt-siblings-loading');
        }
      });

    return true; // we handled the click
  }

  // ── Click delegation ────────────────────────────────────────────────────────
  document.addEventListener('click', function (event) {
    const anchor = event.target.closest('.shpflt-color-siblings__swatch');
    if (!anchor) return;

    // Current product — clicking its own swatch should be a no-op
    if (anchor.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      return;
    }

    // Attempt AJAX swap; only preventDefault on success (product-info found)
    if (swapSibling(anchor)) {
      event.preventDefault();
    }
    // If swapSibling returns false (standalone context), the <a href> navigates normally
  });

  // ── popstate: re-fetch when user presses Back / Forward ────────────────────
  window.addEventListener('popstate', function (event) {
    const productInfo = document.querySelector('product-info[data-section]');
    if (!productInfo) return;

    const href = window.location.pathname + window.location.search;
    const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    const sep = href.includes('?') ? '&' : '?';
    const requestUrl = `${href}${sep}section_id=${sectionId}`;

    if (activeController) activeController.abort();
    const thisController = new AbortController();
    activeController = thisController;

    productInfo.classList.add('shpflt-siblings-loading');

    fetch(requestUrl, { signal: thisController.signal })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newProductInfo = doc.querySelector('product-info');
        if (!newProductInfo) return;
        // No <head><title> in a section-fragment response (same reason as swapSibling
        // above) — read it off the fetched fragment's own "current" swatch instead.
        const currentSwatch = newProductInfo.querySelector('.shpflt-color-siblings__swatch[aria-current="page"]');
        if (currentSwatch && currentSwatch.dataset.title) document.title = currentSwatch.dataset.title;
        HTMLUpdateUtility.viewTransition(productInfo, newProductInfo);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        window.location.reload(); // safe fallback
      })
      .finally(function () {
        if (activeController === thisController) {
          productInfo.classList.remove('shpflt-siblings-loading');
        }
      });
  });

})();
